import React from 'react';
import { CheckCircle, ShoppingCart, User, BookOpen, MessageCircle } from 'lucide-react';

/**
 * Order confirmation component that displays order success and details
 */
const OrderConfirmation = ({ orderConfirmation, setCurrentView }) => (
  <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center py-12 px-4">
    <div className="max-w-2xl w-full">
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-8 text-center">
          <div className="bg-white/20 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12" />
          </div>
          <h2 className="text-3xl font-bold mb-2">Order Confirmed! 🎉</h2>
          <p className="text-green-100">Your literary adventure is on its way!</p>
        </div>
        
        {/* Order Details */}
        <div className="p-8">
          {orderConfirmation && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-2xl">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <ShoppingCart className="w-6 h-6 mr-2 text-blue-600" />
                  Order Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Order ID</p>
                    <p className="font-mono font-semibold">#{orderConfirmation.id}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Order Date</p>
                    <p className="font-semibold">
                      {new Date(orderConfirmation.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                {orderConfirmation.book && (
                  <div className="mt-6 flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm">
                    <img 
                      src={orderConfirmation.book.image} 
                      alt={orderConfirmation.book.title} 
                      className="w-16 h-20 object-cover rounded-lg shadow-md" 
                    />
                    <div className="flex-1">
                      <h4 className="font-bold text-lg">{orderConfirmation.book.title}</h4>
                      <p className="text-gray-600">{orderConfirmation.book.author}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="text-sm text-gray-500">
                          Qty: {orderConfirmation.quantity}
                        </span>
                        <span className="text-lg font-bold text-green-600">
                          ${orderConfirmation.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {orderConfirmation.image && !orderConfirmation.book && (
                  <div className="mt-6 flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm">
                    <img 
                      src={orderConfirmation.image} 
                      alt="Custom book" 
                      className="w-16 h-20 object-cover rounded-lg shadow-md" 
                    />
                    <div className="flex-1">
                      <h4 className="font-bold text-lg">Custom Book Order</h4>
                      <p className="text-gray-600">Image provided for identification</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="text-sm text-gray-500">
                          Qty: {orderConfirmation.quantity}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <User className="w-6 h-6 mr-2 text-purple-600" />
                  Customer Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Name</p>
                    <p className="font-semibold">{orderConfirmation.userInfo.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Email</p>
                    <p className="font-semibold">{orderConfirmation.userInfo.email}</p>
                  </div>
                  {orderConfirmation.userInfo.phone && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600 mb-1">Phone</p>
                      <p className="font-semibold">{orderConfirmation.userInfo.phone}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-center space-y-4">
                <p className="text-gray-600">
                  📧 A confirmation email has been sent to your inbox with all the details.
                </p>
                <p className="text-sm text-gray-500">
                  Expected delivery: 3-5 business days
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setCurrentView('home')}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-bold py-4 rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Order More Books</span>
                </button>
                
                <button
                  onClick={() => setCurrentView('chat')}
                  className="flex-1 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white font-bold py-4 rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Chat with AI</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default OrderConfirmation;
